<template>
  <div class="modal-card" style="min-width: 720px; max-width: 900px;">
    <header class="modal-card-head">
      <p class="modal-card-title">Asignar {{ cargoLabel }}</p>
      <button type="button" class="delete" @click="$emit('close')" />
    </header>

    <section class="modal-card-body" ref="modalBody">
      <b-message v-if="errores.length > 0" type="is-danger" has-icon>
        <div v-for="(e, i) in errores" :key="i">{{ e }}</div>
      </b-message>

      <!-- Persona actual -->
      <div v-if="personaActual && personaActual.nombre" class="box mb-4">
        <p class="title is-6 mb-2">Persona actual en el cargo</p>
        <p><strong>{{ personaActual.nombre }}</strong></p>
        <p v-if="personaActual.start_date" class="has-text-grey is-size-7">
          Activo/a desde: {{ personaActual.start_date }}
        </p>
        <b-field label="Fecha de término para persona actual (debe ser anterior a hoy)" class="mt-3">
          <b-datepicker
            v-model="fechaTerminoActual"
            :max-date="maxFechaTermino"
            placeholder="Seleccionar fecha..."
            icon="calendar-alt"
            trap-focus
            locale="es-CL"
            editable
          />
        </b-field>
      </div>

      <!-- Selección de función (solo si hay múltiples opciones) -->
      <div v-if="functionOptions.length > 1" class="box mb-4">
        <p class="title is-6 mb-2">Función a asignar</p>
        <b-field>
          <div v-for="opt in functionOptions" :key="opt.id" class="mb-2">
            <b-radio v-model="selectedFunctionId" :native-value="opt.id">
              {{ opt.name }}
            </b-radio>
          </div>
        </b-field>
      </div>

      <!-- Búsqueda de nueva persona -->
      <div class="box mb-4">
        <p class="title is-6 mb-2">Nueva persona a asignar</p>

        <b-field>
          <b-input
            v-model="busqueda"
            icon="search"
            placeholder="Buscar por nombre (mínimo 3 caracteres)..."
            expanded
            @keyup.enter.native="buscarPersona"
          />
          <p class="control">
            <b-button @click="buscarPersona" class="is-primary" :loading="buscando">Buscar</b-button>
          </p>
        </b-field>

        <b-table
          v-if="resultados.length > 0"
          :data="resultados"
          hoverable
          striped
          :paginated="resultados.length > 8"
          :per-page="8"
          class="mt-3"
        >
          <b-table-column field="name" label="Nombre" v-slot="props">
            {{ props.row.name }}
          </b-table-column>
          <b-table-column field="unit" label="Unidad" v-slot="props">
            {{ props.row.unit }}
          </b-table-column>
          <b-table-column field="corporate_mail" label="Correo" v-slot="props">
            {{ props.row.corporate_mail || props.row.personal_mail }}
          </b-table-column>
          <b-table-column label="" v-slot="props" width="110">
            <b-button size="is-small" type="is-primary is-outlined" @click="seleccionarPersona(props.row)">
              Seleccionar
            </b-button>
          </b-table-column>
        </b-table>

        <div v-if="personaSeleccionada" class="notification is-primary is-light mt-3">
          <b-icon icon="check-circle" size="is-small" />
          <strong> Seleccionado/a:</strong> {{ personaSeleccionada.name }}
          <a class="ml-3 has-text-grey is-size-7" @click="limpiarPersona">(cambiar)</a>
        </div>
      </div>

      <!-- Nombramiento vigente (PersonUnit) -->
      <div v-if="personaSeleccionada" class="box mb-4">
        <p class="title is-6 mb-2">Nombramiento vigente a asignar</p>
        <div v-if="cargandoPersonUnits" class="has-text-centered py-3">
          <b-icon icon="spinner" custom-class="fa-spin" />
          Cargando nombramientos...
        </div>
        <div v-else-if="personUnits.length === 0" class="has-text-grey is-size-7">
          La persona no tiene nombramientos vigentes registrados.
        </div>
        <b-field v-else label="Seleccionar nombramiento (opcional)">
          <b-select v-model="selectedPersonUnitId" placeholder="Sin nombramiento para asignar al cargo" expanded>
            <option :value="null">Sin nombramiento para asignar al cargo</option>
            <option v-for="pu in personUnits" :key="pu.id" :value="pu.id">
              {{ pu.unit_name }} — {{ pu.internal_position || 'Sin cargo' }}
              <template v-if="pu.appointment_type"> ({{ pu.appointment_type }})</template>
              <template v-if="pu.start_date"> · desde {{ pu.start_date }}</template>
            </option>
          </b-select>
        </b-field>
      </div>

      <!-- Fechas del nuevo nombramiento -->
      <div class="box">
        <p class="title is-6 mb-2">Fechas del nuevo cargo</p>
        <div class="columns">
          <div class="column">
            <b-field label="Fecha de inicio *">
              <b-datepicker
                v-model="fechaInicio"
                placeholder="Seleccionar..."
                icon="calendar-alt"
                trap-focus
                locale="es-CL"
                editable
              />
            </b-field>
          </div>
          <div class="column">
            <b-field label="Fecha de término (opcional)">
              <b-datepicker
                v-model="fechaTerminoNueva"
                :min-date="fechaInicio || undefined"
                placeholder="Seleccionar..."
                icon="calendar-alt"
                trap-focus
                locale="es-CL"
                editable
              />
            </b-field>
          </div>
        </div>
      </div>
    </section>

    <footer class="modal-card-foot" style="justify-content: flex-end; gap: 0.5rem;">
      <b-button @click="$emit('close')">Cancelar</b-button>
      <b-button type="is-primary" @click="guardar" :loading="guardando">Guardar</b-button>
    </footer>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ModalAsignarAutoridad",
  props: {
    cargoLabel:      { type: String, required: true },
    personaActual:   { type: Object, default: null },   // { nombre, person_function_id, start_date }
    functionOptions: { type: Array,  required: true },  // [{ id, name }]
    unitId:          { type: Number, default: null },
  },
  data() {
    const ayer = new Date();
    ayer.setDate(ayer.getDate() - 1);
    return {
      busqueda:             "",
      resultados:           [],
      personaSeleccionada:  null,
      buscando:             false,
      guardando:            false,
      errores:              [],
      fechaTerminoActual:   null,
      fechaInicio:          null,
      fechaTerminoNueva:    null,
      selectedFunctionId:   this.functionOptions?.length > 0 ? this.functionOptions[0].id : null,
      maxFechaTermino:      ayer,
      personUnits:          [],
      selectedPersonUnitId: null,
      cargandoPersonUnits:  false,
    };
  },
  methods: {
    async buscarPersona() {
      if (!this.busqueda || this.busqueda.trim().length < 3) {
        this.$buefy.toast.open({ message: "Ingrese al menos 3 caracteres para buscar", type: "is-warning" });
        return;
      }
      this.buscando = true;
      this.resultados = [];
      try {
        const participantes = encodeURIComponent(JSON.stringify({ checkedRows: [] }));
        const res = await axios.get(
          `/people/${encodeURIComponent(this.busqueda.trim())}/${participantes}/search_people.json`
        );
        this.resultados = res.data?.participants || [];
        if (this.resultados.length === 0) {
          this.$buefy.toast.open({ message: "No se encontraron personas con ese nombre", type: "is-info" });
        }
      } catch (e) {
        this.$buefy.toast.open({ message: "Error al buscar personas", type: "is-danger" });
      }
      this.buscando = false;
    },

    async seleccionarPersona(row) {
      this.personaSeleccionada  = row;
      this.resultados           = [];
      this.busqueda             = "";
      this.personUnits          = [];
      this.selectedPersonUnitId = null;
      this.cargandoPersonUnits  = true;
      try {
        const res = await axios.get(`/people/${row.person_id}/active_person_units.json`);
        this.personUnits = res.data?.person_units || [];
        if (this.personUnits.length === 1) {
          this.selectedPersonUnitId = this.personUnits[0].id;
        }
      } catch (e) {
        this.personUnits = [];
      }
      this.cargandoPersonUnits = false;
    },

    limpiarPersona() {
      this.personaSeleccionada  = null;
      this.personUnits          = [];
      this.selectedPersonUnitId = null;
    },

    formatDate(d) {
      if (!d) return null;
      const y   = d.getFullYear();
      const m   = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    async guardar() {
      this.errores = [];

      if (!this.personaSeleccionada) {
        this.errores.push("Debe seleccionar una persona.");
      }
      if (!this.fechaInicio) {
        this.errores.push("Debe ingresar la fecha de inicio del nuevo nombramiento.");
      }
      if (this.functionOptions.length > 1 && !this.selectedFunctionId) {
        this.errores.push("Debe seleccionar qué función asignar.");
      }
      if (this.personaActual?.person_function_id && this.fechaTerminoActual) {
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        if (this.fechaTerminoActual >= hoy) {
          this.errores.push("La fecha de término de la persona actual debe ser anterior a hoy.");
        }
      }
      if (this.fechaInicio && this.fechaTerminoNueva && this.fechaTerminoNueva <= this.fechaInicio) {
        this.errores.push("La fecha de término del nuevo nombramiento debe ser posterior a la fecha de inicio.");
      }

      if (this.errores.length > 0) {
        this.$refs.modalBody.scrollTop = 0;
        return;
      }

      this.guardando = true;
      try {
        const payload = {
          function_id:            this.selectedFunctionId || this.functionOptions[0].id,
          person_id:              this.personaSeleccionada.person_id,
          start_date:             this.formatDate(this.fechaInicio),
          end_date:               this.formatDate(this.fechaTerminoNueva),
          old_person_function_id: this.personaActual?.person_function_id || null,
          old_end_date:           this.formatDate(this.fechaTerminoActual),
          unit_id:                this.unitId,
          person_unit_id:         this.selectedPersonUnitId,
        };

        await axios.post("/person_functions/assign_authority.json", payload);

        this.$buefy.dialog.alert({
          message:     "Autoridad asignada con éxito.",
          type:        "is-success",
          confirmText: "Ok",
        });
        this.$emit("saved");
        this.$emit("close");
      } catch (error) {
        const data = error.response?.data;
        const msgs = data?.errors || data?.error || ["Error al guardar. Intente nuevamente."];
        this.errores = Array.isArray(msgs) ? msgs : [msgs];
        this.$refs.modalBody.scrollTop = 0;
      }
      this.guardando = false;
    },
  },
};
</script>
